import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import Shimmer from "../Shimmer/Shimmer";
import "./styles.scss";
import { POSATypeJavascriptDTO } from "@tootsweet/model/lottery/POSAType";
import { svgRenderer } from "../../utils/svgRenderer";
import {
  getWidthForPOSAFormat,
  getHeightForPOSAFormat,
  SVGRenderOptions,
  resizeImage,
  getImageDimensions,
  calculateAspectRatioFit,
  RENDER_DENSITY
} from "kadow-svg-utils";
import { LANGUAGE } from "../../utils/i18n/i18n";
import { POSA_CDN_PREFIX } from "../../utils/constants";
import CONFIG from "../../model/TSConfig";

interface LogoCacheEntry {
  blob: Blob;
  width: number;
  height: number;
}

const logoCache = new Map<string, LogoCacheEntry>();

const POSA_SVG_CDN_URL = `${CONFIG.cdn_url}/jetpack/${POSA_CDN_PREFIX}/svg`;

// Supersampling applied on top of the on-screen logo box size so the rasterized
// logo stays crisp on high-DPI previews. Multiplied by RENDER_DENSITY so the logo
// bitmap scales in lockstep with the PNG/PDF print density (see ExportSVGtoPNG).
const LOGO_PREVIEW_SUPERSAMPLE = 2;
const LOGO_RASTER_FACTOR = LOGO_PREVIEW_SUPERSAMPLE * RENDER_DENSITY;

interface Props {
  posa: POSATypeJavascriptDTO;
  eventId: string;
  posaDestination: string;
  isEditing: boolean;
  existingSvgId?: string;
  logo_url?: string;
  configId: string;
  primary: string;
  accent: string;
  background: string;
  subtitle: string;
  logoZoom?: number;
  pegLogoX?: boolean;
  onFinishedRender?: () => void;
  wording?: string;
}

export default function SVGPreview(props: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const containerSVGUniqueID = useMemo(() => {
    return svgRenderer.generateContainerId(props.posa._id);
  }, [props.posa]);

  const DEFAULT_WIDTH = useMemo(() => {
    return getWidthForPOSAFormat(props.posa.format);
  }, [props.posa]);

  const DEFAULT_HEIGHT = useMemo(() => {
    return getHeightForPOSAFormat(props.posa.format);
  }, [props.posa]);

  const renderOptions: SVGRenderOptions = useMemo(() => {
    return {
      posa: props.posa,
      eventId: props.eventId,
      posaDestination: props.posaDestination,
      cdnUrl: POSA_SVG_CDN_URL,
      colors: {
        primary: props.primary,
        accent: props.accent,
        background: props.background,
        subtitle: props.subtitle
      },
      logo_url: props.logo_url,
      format: props.posa.format,
      utmName: props.posa.utmName,
      isNoCustom: !props.posa.isCustom,
      isEditing: props.isEditing,
      configId: props.configId,
      svgSize: DEFAULT_WIDTH,
      mustRenderQRCode: false,
      logoZoom: props.logoZoom,
      pegLogoX: props.pegLogoX,
      language: LANGUAGE,
      wording: props.wording,
      getLogoBase64ForWidthAndHeight: async (
        width: number,
        height: number
      ): Promise<string> => {
        if (!props.logo_url) {
          return "";
        }

        let cacheEntry = logoCache.get(props.logo_url);

        if (!cacheEntry) {
          const response = await fetch(
            `${CONFIG.cdn_url}/jetpack/logos/${props.logo_url}`
          );
          const blob = await response.blob();
          const dimensions = await getImageDimensions(blob);
          cacheEntry = {
            blob,
            width: dimensions.width,
            height: dimensions.height
          };
          logoCache.set(props.logo_url, cacheEntry);
        }

        const targetDimensions = calculateAspectRatioFit(
          cacheEntry.width,
          cacheEntry.height,
          width * LOGO_RASTER_FACTOR,
          height * LOGO_RASTER_FACTOR
        );

        const result = await resizeImage(
          cacheEntry.blob,
          targetDimensions.width,
          targetDimensions.height
        );

        return result?.base64 || "";
      }
    };
  }, [
    props.posa,
    props.eventId,
    props.posaDestination,
    props.primary,
    props.accent,
    props.background,
    props.subtitle,
    props.logo_url,
    props.isEditing,
    props.configId,
    props.logoZoom,
    props.pegLogoX,
    DEFAULT_WIDTH,
    props.wording
  ]);

  const downloadSVGFromCDNAndRenderIt = async () => {
    setIsLoading(true);

    try {
      const res = await svgRenderer.renderSVGToContainer(
        containerSVGUniqueID,
        renderOptions
      );

      if (res) {
        await svgRenderer.renderLogoAndQRCode(renderOptions, res.svgElement);
        props.onFinishedRender?.();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const STYLE = useMemo<CSSProperties>(() => {
    return {
      position: "relative",
      width: DEFAULT_WIDTH,
      minHeight: DEFAULT_HEIGHT
    };
  }, [DEFAULT_WIDTH, DEFAULT_HEIGHT]);

  useEffect(() => {
    if (!containerSVGUniqueID) {
      return;
    }

    void downloadSVGFromCDNAndRenderIt();
  }, [containerSVGUniqueID, renderOptions]);

  useEffect(() => {
    logoCache.clear();
  }, [props.logo_url]);

  return (
    <div style={STYLE}>
      <div id={containerSVGUniqueID} className="svg-preview-container"></div>
      {isLoading && (
        <div style={{ position: "absolute", bottom: 0, left: 0 }}>
          <Shimmer width={DEFAULT_WIDTH} height={DEFAULT_HEIGHT} />
        </div>
      )}
    </div>
  );
}
